import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Check, Search } from "lucide-react";

const TravelFormSelect = ({ label, name, value, onChange, options = [], required = false, className = "", error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options?.filter((opt) => opt.label.toLowerCase().includes(searchTerm.toLowerCase())) || [];

  const selectedOption = options?.find((opt) => opt.value === value);

  const handleSelect = (selectedValue) => {
    onChange({
      target: {
        name,
        value: selectedValue,
      },
    });
    setIsOpen(false);
    setSearchTerm("");
  };

  const isFloating = isOpen || (value && value.toString().trim().length > 0);

  return (
    <div
      className={`relative flex flex-col rounded-xl bg-transparent ${className}`}
      ref={dropdownRef}>
      {label && (
        <label
          htmlFor={name}
          className={`pointer-events-none absolute z-10 bg-white px-1 transition-all duration-200 ease-in-out ${isFloating ? `-top-2.5 left-3 text-[11px] font-bold ${error ? "text-red-500" : "text-[#2D0B31]"}` : `left-3 top-3.5 text-sm font-medium ${error ? "text-red-500" : "text-gray-500"}`}`}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex w-full items-center justify-between rounded-xl border bg-white px-4 py-3.5 text-sm shadow-sm transition-all duration-200 focus:outline-none focus:ring-1 ${
          isOpen ? "border-[#2D0B31] ring-1 ring-[#2D0B31]" : error ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-200 hover:border-gray-300 hover:shadow-md"
        }`}>
        <span className={selectedOption ? "text-gray-900" : "text-gray-400"}>{selectedOption ? selectedOption.label : isFloating ? "- Select -" : ""}</span>

        <ChevronDown
          size={18}
          className={`transition-transform duration-200 ${isOpen ? "rotate-180 text-[#2D0B31]" : "text-gray-400"}`}
        />
      </button>
      {isOpen && (
        <div className="animate-in fade-in slide-in-from-top-2 absolute top-[105%] z-[100] mt-1 max-h-60 w-full overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl ring-1 ring-black/5 duration-200">
          {options.length > 10 && (
            <div className="sticky top-0 z-10 border-b border-gray-100 bg-white/95 px-3 py-2 backdrop-blur-sm">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-2.5 top-2.5 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-md border-none bg-gray-50 py-2 pl-8 pr-3 text-sm text-gray-900 focus:bg-gray-100 focus:outline-none focus:ring-0"
                />
              </div>
            </div>
          )}

          <ul className="custom-scrollbar max-h-48 overflow-y-auto py-1">
            {filteredOptions.length === 0 ? (
              <li className="px-4 py-3 text-center text-sm text-gray-500">No results found</li>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <li
                    key={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    className={`flex cursor-pointer items-center justify-between px-4 py-2.5 text-sm transition-colors ${isSelected ? "bg-[#2D0B31]/5 font-bold text-[#2D0B31]" : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"}`}>
                    {opt.label}
                    {isSelected && (
                      <Check
                        size={16}
                        className="text-[#2D0B31]"
                      />
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
      {error && <span className="animate-in fade-in slide-in-from-top-1 mt-1.5 text-[11px] font-bold text-red-500 pl-1">{error}</span>}
    </div>
  );
};

export default TravelFormSelect;
